import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  CategoryStoreActions,
  DeviceStoreActions,
  DeviceStoreSelectors,
  LocationsRouterStoreSelectors,
} from '@era/stores';
import { Store } from '@ngrx/store';
import { DeviceLinkSelection, isDeviceLinkSelectionValid } from './device-link-selection';
import { DeviceLinkSelectorComponent } from './device-link-selector/device-link-selector.component';
import {
  selectDeviceLinkChildren,
  selectDeviceLinkLocations,
  selectDeviceLinkUnavailableCategories,
} from './device-link.selectors';

@Component({
  selector: 'era-device-link',
  templateUrl: './device-link.container.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [DeviceLinkSelectorComponent, RouterLink],
})
export class DeviceLinkContainerComponent {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  readonly device = this.store.selectSignal(DeviceStoreSelectors.selectCurrentDevice);
  readonly locations = this.store.selectSignal(selectDeviceLinkLocations);
  readonly children = this.store.selectSignal(selectDeviceLinkChildren);

  private readonly unavailableCategories = this.store.selectSignal(selectDeviceLinkUnavailableCategories);
  private readonly brandCategoryId = this.store.selectSignal(LocationsRouterStoreSelectors.selectBrandId);
  private readonly routeData = toSignal(this.route.data, { initialValue: this.route.snapshot.data });

  // The mode as defined by the route. Spare, Unassign and Unavailable are special
  readonly mode = computed(() => {
    switch (this.routeData()['categoryType']) {
      case 'Location':
        return 'spare';
      case 'Brand':
        return 'unassign';
      case 'None':
        return 'unavailable';
      default:
        return 'assign';
    }
  });

  readonly title = computed(
    () => ({ assign: 'Assign', spare: 'Spare', unassign: 'Unassign', unavailable: 'Unavailable' })[this.mode()]
  );

  readonly showChildCategorySelector = computed(() => this.mode() === 'assign' || this.mode() === 'spare');

  private readonly editedSelection = signal<DeviceLinkSelection | undefined>(undefined);

  // What is currently selected in the UI.
  readonly selection = computed<DeviceLinkSelection>(() => {
    const edited = this.editedSelection();
    const device = this.device();

    // Guard: return what the user selected by default.
    if (edited) {
      return edited;
    }

    // If there is no selection, fallback to the current device selection.
    const locationCategoryId = device?.locationCategoryId ?? undefined;
    if (this.mode() === 'spare') {
      return {
        locationCategoryId,
        categoryType: 'Location',
        categoryId: locationCategoryId,
      };
    }

    const categoryId = device?.categoryId || undefined;
    const category = this.children().find((child) => child.id === categoryId);
    return {
      locationCategoryId,
      categoryId: category?.id,
      categoryType: category?.categoryType,
    };
  });

  private readonly unavailableCategory = computed(() =>
    this.unavailableCategories().find((category) => category.parent?.categoryId === this.brandCategoryId())
  );

  private readonly targetCategoryId = computed(() => {
    if (this.mode() === 'unassign') return undefined;
    if (this.mode() === 'unavailable') return this.unavailableCategory()?.categoryId;

    const selection = this.selection();
    return selection.categoryId;
  });

  readonly isValidSelection = computed(() => {
    const mode = this.mode();
    const targetCategoryId = this.targetCategoryId();
    const selection = this.selection();

    if (mode === 'unassign' && targetCategoryId === undefined) {
      return true;
    }

    if (mode === 'unavailable' && targetCategoryId !== undefined) {
      return true;
    }

    return isDeviceLinkSelectionValid(selection, this.locations(), this.children());
  });

  constructor() {
    effect(() => {
      this.mode();
      this.editedSelection.set(undefined);
    });

    effect(() => {
      const locationCategoryId = this.selection().locationCategoryId;
      if (locationCategoryId) {
        this.store.dispatch(
          CategoryStoreActions.CategoryLoadActions.assignableChildrenRequested({ locationCategoryId })
        );
      }
    });

    effect(() => {
      const brandCategoryId = this.brandCategoryId();
      if (brandCategoryId) {
        this.store.dispatch(CategoryStoreActions.CategoryLoadActions.unavailableCategoryRequested({ brandCategoryId }));
      }
    });
  }

  updateSelection(selection: DeviceLinkSelection): void {
    this.editedSelection.set(selection);
  }

  markAsCategory(): void {
    const deviceId = this.device()?.udid;
    const categoryId = this.targetCategoryId();

    // Guard: we must have a device in order to be able to assign it.
    if (!deviceId) {
      return;
    }

    this.store.dispatch(DeviceStoreActions.DeviceAssignmentViewActions.categoryAssigned({ deviceId, categoryId }));
    void this.router.navigate(['../'], { relativeTo: this.route });
  }
}
